import { useT } from '@transifex/react';
import { Text, VStack } from '@vidahealth/vida-ui';
import { useLocalSearchParams } from 'expo-router';
import { useState } from 'react';
import { View } from 'react-native';

import { logError, logInfo, trackEvent, useFeatureFlagVariant } from '@/modules/analytics';
import { createUser } from '@/modules/api';
import { formatBirthdayForDisplay } from '@/modules/localization';
import { FEATURE_FLAGS, useRulesEngine } from '@/modules/rulesEngine';
import { PhoneField, Screen, SensitiveField, TextField } from '@/modules/ui';

import { useAccountForm } from '../hooks/useAccountForm';
import { useAccountLegalDocuments } from '../hooks/useAccountLegalDocuments';
import { useSignIn } from '../hooks/useSignIn';
import { accountSchema } from '../schema/accountSchema';
import {
  ACCOUNT_CREATED_EVENT,
  accountCreatedEvent,
  createUserErrorContext,
} from '../utils/accountCreationEvents';
import { buildCreateUserPayload } from '../utils/buildCreateUserPayload';
import { ALREADY_MATCHED_CODE, getCreateUserError } from '../utils/getCreateUserError';

import { AccountHeader } from './AccountHeader';
import { AgreementsSection } from './AgreementsSection';
import { ExistingAccountBottomSheet } from './ExistingAccountBottomSheet';
import { Footer } from './Footer';
import { SignInBottomSheet } from './SignInBottomSheet';

// Brand-new user: collects name + email + phone + DOB + password + consents and
// POSTs to create a user + customer profile in one shot. Handles the two
// existing-account shapes createUser returns (matched / email-uniqueness) via the
// existing-account drawer.
//
// `autoOpenSignIn` seeds the sign-in sheet open — used as the dev backdrop for a
// PURL already-enrolled token, where the member should sign in over this form.
export function NormalAccountCreationScreen({
  autoOpenSignIn = false,
}: {
  autoOpenSignIn?: boolean;
} = {}) {
  const t = useT();
  // `?org=` personalizes the title with the org display name.
  const { org } = useLocalSearchParams<{ org?: string }>();

  const { user, refreshUser } = useRulesEngine();
  const { legalGroups, legalAdditionalLanguages, legalError, isLegalUnavailable } =
    useAccountLegalDocuments();
  const { control, handleSubmit, persistDraft } = useAccountForm(accountSchema, legalGroups);
  const { signInOpen, setSignInOpen, onSignIn } = useSignIn(autoOpenSignIn);

  const [isSubmitting, setIsSubmitting] = useState(false);
  // General (non-field) submit failure surface.
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [existingAccountOpen, setExistingAccountOpen] = useState(false);
  // Existing-account drawer content, set per failure shape (see catch below).
  // Store raw values rather than a pre-translated heading, so the matched heading
  // re-translates if Transifex pushes mid-drawer. ALREADY_MATCHED → masked email +
  // no server message; plain email-uniqueness → the server's own message.
  const [existingAccountMaskedEmail, setExistingAccountMaskedEmail] = useState<string | null>(null);
  const [existingAccountServerMessage, setExistingAccountServerMessage] = useState<string | null>(
    null
  );

  // in-year-revenue experiment: the test arm's subheader tells the member to
  // enter their name as their organization has it, heading off the eligibility
  // mismatch this screen's data causes downstream.
  const { value: inYearRevenueVariant, isLoading: inYearRevenueLoading } = useFeatureFlagVariant<
    'control' | 'test'
  >(FEATURE_FLAGS.IN_YEAR_REVENUE_ONBOARDING);
  const guidesLegalNameEntry = inYearRevenueVariant === 'test';

  const payingOrg = user?.payingOrganization;
  const orgName = payingOrg ? payingOrg.name : org;
  const title = orgName
    ? t('Good news! Vida is offered to you by {org}', { org: orgName })
    : t('Good news! You may be eligible for Vida through your employer or health plan');

  // Hold the subheader until the arm resolves instead of assuming control
  // mid-read: the flag read lands in an effect, i.e. after the first paint, so
  // serving control copy here would show every test-arm member both arms of an
  // experiment whose only difference *is* this copy. ScreenHeading renders the
  // title alone while this is undefined.
  const subtitle = inYearRevenueLoading
    ? undefined
    : guidesLegalNameEntry
      ? t('Enter your information just as it appears on your health insurance card or pay stub.')
      : t('Create an account to confirm your eligibility, using your personal information.');

  // Defensive: if a backend user already owns these (e.g. a returning or
  // partially-registered account that lands back on account creation), render
  // them pre-filled (via the draft hydrated by mapUserDataToDraft) + read-only
  // rather than editable. Mirrors Sparta's AccountCreationPage disabledFields.
  const disabledFields = {
    firstName: !!user?.firstName,
    lastName: !!user?.lastName,
    email: !!user?.email,
  };

  const onExistingAccountLogin = () => {
    setExistingAccountOpen(false);
    onSignIn();
  };

  const onContinue = handleSubmit(async (values) => {
    setExistingAccountOpen(false);
    setSubmitError(null);
    persistDraft(values);

    try {
      setIsSubmitting(true);
      const createdUser = await createUser(buildCreateUserPayload(values, legalGroups));
      // refreshUser() flips authStatus to 'authenticated' and recomputes
      // currentRoute — without it the resolver leaves the user on /account-creation.
      await refreshUser();
      trackEvent(ACCOUNT_CREATED_EVENT, accountCreatedEvent(createdUser));
    } catch (error) {
      // createUser returns two existing-account shapes (documented on
      // getCreateUserError) — branch on them as Sparta does. Both are EXPECTED
      // user states, not faults, so they don't go to logError (which feeds
      // signup-failure alerts); only a genuine failure does.
      const apiError = getCreateUserError(error);
      if (apiError?.code === ALREADY_MATCHED_CODE) {
        // (1) Matched to a known account — no server message, so the drawer
        // renders the fixed copy via <T> + the recognizable masked email. Record
        // it at INFO (mirrors Sparta's Datadog.sendInfo) so it's visible without
        // polluting error dashboards.
        logInfo('You already have an account', {
          explanationCode: ALREADY_MATCHED_CODE,
          alreadyMatchedEmail: apiError.maskedEmail,
        });
        setExistingAccountMaskedEmail(apiError.maskedEmail ?? null);
        setExistingAccountServerMessage(null);
        setExistingAccountOpen(true);
      } else if (apiError?.error) {
        // (2) Plain email-uniqueness violation — the backend returns only a
        // human-readable, LOCALIZED message, so gate on the message's presence,
        // not an English substring (which would miss every non-English locale).
        setExistingAccountServerMessage(apiError.error);
        setExistingAccountMaskedEmail(null);
        setExistingAccountOpen(true);
      } else {
        // Genuine failure (network/timeout/5xx/unrecognized) — log at ERROR for
        // alerting, with the raw message so a non-API failure still carries
        // something to triage.
        logError('create user error', {
          ...createUserErrorContext(apiError),
          error: error instanceof Error ? error.message : String(error),
        });
        setSubmitError(t('Could not create your account. Please try again.'));
      }
    } finally {
      setIsSubmitting(false);
    }
  });

  return (
    <>
      <Screen>
        <VStack space='2xl'>
          <AccountHeader title={title} subtitle={subtitle} />

          {submitError ? (
            <Text size='sm' className='text-error-700'>
              {submitError}
            </Text>
          ) : null}

          <View className='w-full flex-col gap-6 sm:flex-row'>
            <View className='flex-1'>
              <TextField
                control={control}
                name='firstName'
                placeholder={t('Legal first name')}
                autoComplete='given-name'
                textContentType='givenName'
                isDisabled={disabledFields.firstName}
                testID='account-creation-first-name'
              />
            </View>
            <View className='flex-1'>
              <TextField
                control={control}
                name='lastName'
                placeholder={t('Legal last name')}
                autoComplete='family-name'
                textContentType='familyName'
                isDisabled={disabledFields.lastName}
                testID='account-creation-last-name'
              />
            </View>
          </View>

          <TextField
            control={control}
            name='email'
            placeholder={t('Email address')}
            autoCapitalize='none'
            autoComplete='email'
            keyboardType='email-address'
            inputMode='email'
            textContentType='emailAddress'
            isDisabled={disabledFields.email}
            testID='account-creation-email'
          />
          <PhoneField
            control={control}
            name='phone'
            placeholder={t('Phone number (optional)')}
            testID='account-creation-phone'
          />

          <TextField
            control={control}
            name='birthday'
            label={t('Date of birth')}
            placeholder={t('MM / DD / YYYY')}
            inputMode='numeric'
            keyboardType='number-pad'
            maxLength={14}
            format={formatBirthdayForDisplay}
            testID='account-creation-dob'
          />
          <SensitiveField
            control={control}
            name='password'
            placeholder={t('Password')}
            autoComplete='new-password'
            textContentType='newPassword'
            testID='account-creation-password'
          />

          <AgreementsSection
            control={control}
            legalGroups={legalGroups}
            legalError={legalError}
            showSms
          />

          <Footer
            showSignIn
            isSubmitting={isSubmitting || isLegalUnavailable}
            additionalLanguages={legalAdditionalLanguages}
            onContinue={onContinue}
            onSignIn={onSignIn}
          />
        </VStack>
      </Screen>

      <SignInBottomSheet isOpen={signInOpen} onClose={() => setSignInOpen(false)} />

      <ExistingAccountBottomSheet
        isOpen={existingAccountOpen}
        onClose={() => setExistingAccountOpen(false)}
        onLogin={onExistingAccountLogin}
        serverMessage={existingAccountServerMessage}
        maskedEmail={existingAccountMaskedEmail}
      />
    </>
  );
}
